<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { INITIAL_PLAYERS } from '@/services/mockData';
import type { Team, PlayerProfile, PaymentType } from '@/types/models';
import AppBadge from '@/components/ui/AppBadge.vue';
import AppModal from '@/components/ui/AppModal.vue';
import { useToastNotification } from '@/composables/useToastNotification';
import { NButton, NInput, NSelect } from 'naive-ui';
import {
  Building2,
  Plus,
  Search,
  Link,
  Unlink,
  Send,
  Trophy,
  Swords,
  UserCheck,
  CheckCircle2
} from 'lucide-vue-next';

const { t } = useI18n();
const { activeTenant, managedTeams } = useTenant();
const { notifySuccess, notifyInfo } = useToastNotification();

const activeTab = ref<'TEAMS' | 'PLAYERS'>('TEAMS');

// Teams Master state
const tenantTeams = computed(() => managedTeams.value.filter(t => t.tenantId === activeTenant.value.id));
const teamSearchQuery = ref('');

const filteredTeams = computed(() => {
  return tenantTeams.value.filter(team => {
    const q = teamSearchQuery.value.toLowerCase();
    return (
      team.name.toLowerCase().includes(q) ||
      (team.leagueName || '').toLowerCase().includes(q) ||
      team.division.toLowerCase().includes(q) ||
      team.headCoach.toLowerCase().includes(q)
    );
  });
});

// Players Master state
const masterPlayers = ref<PlayerProfile[]>([...INITIAL_PLAYERS]);
const playerSearchQuery = ref('');
const teamFilter = ref<string>('ALL');

const filteredPlayers = computed(() => {
  return masterPlayers.value
    .filter(p => p.tenantId === activeTenant.value.id)
    .filter(p => {
      if (teamFilter.value !== 'ALL' && p.teamId !== teamFilter.value) return false;
      const q = playerSearchQuery.value.toLowerCase();
      const name = `${p.firstName} ${p.lastName}`.toLowerCase();
      return name.includes(q) || String(p.jerseyNumber).includes(q) || (p.linkedUserEmail || '').toLowerCase().includes(q);
    });
});

// --- Own Team Toggle Handler ---
const toggleOwnTeam = (team: Team) => {
  team.isOwnTeam = !team.isOwnTeam;
  const roleName = team.isOwnTeam ? t('master.ownClub') : t('master.opponent');
  notifySuccess(
    team.isOwnTeam ? t('master.markedOwnTeam') : t('master.markedOpponent'),
    t('master.markedDesc', { name: team.name, role: roleName })
  );
};

// --- Link Player to Tenant User Modal ---
const isLinkModalOpen = ref(false);
const selectedPlayerForLink = ref<PlayerProfile | null>(null);
const linkUserEmail = ref('');

const openLinkModal = (player: PlayerProfile) => {
  selectedPlayerForLink.value = player;
  linkUserEmail.value = player.email || player.linkedUserEmail || '';
  isLinkModalOpen.value = true;
};

const handleConfirmUserLink = () => {
  if (!selectedPlayerForLink.value || !linkUserEmail.value) return;

  const player = selectedPlayerForLink.value;
  player.isLinkedToUser = true;
  player.linkStatus = 'LINKED';
  player.linkedUserEmail = linkUserEmail.value;
  player.linkedUserFullName = `${player.firstName} ${player.lastName}`;
  player.linkedUserId = `usr_${player.id}_clerk`;

  // Update in local master list
  const idx = masterPlayers.value.findIndex(p => p.id === player.id);
  if (idx !== -1) {
    masterPlayers.value[idx] = { ...player };
  }

  isLinkModalOpen.value = false;
  notifySuccess(
    t('master.userLinked'),
    t('master.userLinkedMsg', { num: player.jerseyNumber, name: player.lastName, email: linkUserEmail.value })
  );
};

const handleUnlinkUser = (player: PlayerProfile) => {
  player.isLinkedToUser = false;
  player.linkStatus = 'UNLINKED';
  player.linkedUserId = undefined;
  player.linkedUserEmail = undefined;
  player.linkedUserFullName = undefined;

  const idx = masterPlayers.value.findIndex(p => p.id === player.id);
  if (idx !== -1) {
    masterPlayers.value[idx] = { ...player };
  }

  notifyInfo(
    t('master.userUnlinked'),
    t('master.userUnlinkedMsg', { num: player.jerseyNumber, name: player.lastName })
  );
};

const sendInviteLink = (player: PlayerProfile) => {
  player.linkStatus = 'INVITED';
  notifySuccess(
    t('master.inviteDispatched'),
    t('master.inviteDispatchedMsg', { target: player.email || player.linkedUserEmail || `${player.firstName} ${player.lastName}` })
  );
};

// --- Add Team Master Modal ---
const isAddTeamModalOpen = ref(false);
const newTeamForm = ref({
  name: '',
  leagueName: 'Premier Showcase Circuit (PSC)',
  division: 'National Elite Division',
  headCoach: '',
  isOwnTeam: true,
  color: '#0284c7',
  paymentType: 'TEAM' as PaymentType,
});

const handleSaveTeamMaster = () => {
  if (!newTeamForm.value.name) return;

  const team: Team = {
    id: `team_${Date.now()}`,
    tenantId: activeTenant.value.id,
    name: newTeamForm.value.name,
    season: '2025 Spring',
    division: newTeamForm.value.division,
    headCoach: newTeamForm.value.headCoach || 'Staff Coach',
    color: newTeamForm.value.color,
    wins: 0,
    losses: 0,
    ties: 0,
    playerCount: 0,
    paymentType: newTeamForm.value.paymentType,
    isOwnTeam: newTeamForm.value.isOwnTeam,
    leagueName: newTeamForm.value.leagueName,
    subscriptions: [
      {
        serviceId: 'BASEBALL_OPS_CORE',
        status: 'ACTIVE',
        paymentType: 'TEAM',
        currentPeriodEnd: '2025-12-31',
      }
    ],
  };

  managedTeams.value.push(team);
  isAddTeamModalOpen.value = false;
  notifySuccess(
    t('master.teamRegistered'),
    t('master.teamRegisteredMsg', { team: team.name, league: team.leagueName })
  );
};

const teamFilterOptions = computed(() => [
  { label: t('master.allTeamsFilter'), value: 'ALL' },
  ...tenantTeams.value.map(tOption => ({
    label: `${tOption.name} (${tOption.isOwnTeam ? t('master.ownClub') : t('master.opponent')})`,
    value: tOption.id
  }))
]);
</script>

<template>
  <div class="space-y-6 animate-in fade-in duration-200">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-800/80">
      <div>
        <div class="flex items-center gap-2">
          <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-100 tracking-tight">
            {{ t('master.title') }}
          </h1>
          <AppBadge variant="accent">{{ t('master.badge') }}</AppBadge>
        </div>
        <p class="mt-1 text-xs sm:text-sm text-slate-400">
          {{ t('master.subtitle', { name: activeTenant.name }) }}
        </p>
      </div>

      <!-- Quick Action Stats -->
      <div class="flex items-center gap-2.5">
        <div class="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-xs flex items-center gap-2">
          <span class="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
          <span class="text-slate-300 font-semibold">
            {{ t('master.ownTeamsCount', { count: tenantTeams.filter(t => t.isOwnTeam).length }) }}
          </span>
        </div>
        <div class="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-xs flex items-center gap-2">
          <span class="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
          <span class="text-slate-300 font-semibold">
            {{ t('master.opponentsCount', { count: tenantTeams.filter(t => !t.isOwnTeam).length }) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Navigation Tabs (Teams Master vs Players Master) -->
    <div class="flex items-center gap-2 border-b border-slate-800">
      <button
        type="button"
        @click="activeTab = 'TEAMS'"
        :class="[
          'px-4 py-2.5 text-xs font-bold transition border-b-2 flex items-center gap-2',
          activeTab === 'TEAMS'
            ? 'border-sky-500 text-sky-400 bg-sky-500/10 rounded-t-xl'
            : 'border-transparent text-slate-400 hover:text-slate-200'
        ]"
      >
        <Building2 class="w-4 h-4" />
        <span>{{ t('master.leagueTeamsTab', { count: tenantTeams.length }) }}</span>
      </button>

      <button
        type="button"
        @click="activeTab = 'PLAYERS'"
        :class="[
          'px-4 py-2.5 text-xs font-bold transition border-b-2 flex items-center gap-2',
          activeTab === 'PLAYERS'
            ? 'border-sky-500 text-sky-400 bg-sky-500/10 rounded-t-xl'
            : 'border-transparent text-slate-400 hover:text-slate-200'
        ]"
      >
        <UserCheck class="w-4 h-4" />
        <span>{{ t('master.playerMasterTab', { count: masterPlayers.filter(p => p.tenantId === activeTenant.id).length }) }}</span>
      </button>
    </div>

    <!-- ========================================================================= -->
    <!-- TAB 1: LEAGUE & TEAMS MASTER (Own Team vs Opponent) -->
    <!-- ========================================================================= -->
    <div v-if="activeTab === 'TEAMS'" class="space-y-4">
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div class="relative max-w-sm flex-1">
          <NInput
            v-model:value="teamSearchQuery"
            :placeholder="t('master.searchTeamsPlaceholder')"
            clearable
            size="medium"
          >
            <template #prefix><Search class="w-4 h-4 text-slate-400" /></template>
          </NInput>
        </div>

        <NButton
          type="primary"
          @click="isAddTeamModalOpen = true"
          class="font-bold text-xs shadow-lg shadow-sky-500/20"
        >
          <template #icon><Plus class="w-4 h-4" /></template>
          {{ t('master.addMasterTeam') }}
        </NButton>
      </div>

      <!-- Teams Master Table -->
      <div class="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="border-b border-slate-800/80 bg-slate-950/40 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                <th class="py-3.5 px-4">{{ t('master.colTeamName') }}</th>
                <th class="py-3.5 px-4">{{ t('master.colLeague') }}</th>
                <th class="py-3.5 px-4">{{ t('master.colDivision') }}</th>
                <th class="py-3.5 px-4">{{ t('master.colCoach') }}</th>
                <th class="py-3.5 px-4 text-center">{{ t('master.colDesignation') }}</th>
                <th class="py-3.5 px-4 text-right">{{ t('master.colActions') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-800/60 font-sans">
              <tr
                v-for="team in filteredTeams"
                :key="team.id"
                class="hover:bg-slate-800/40 transition"
              >
                <!-- Team Name with Color Pip -->
                <td class="py-3.5 px-4">
                  <div class="flex items-center gap-2.5">
                    <span class="w-3.5 h-3.5 rounded-full shrink-0 shadow-sm" :style="{ backgroundColor: team.color || '#0284c7' }" />
                    <div>
                      <span class="font-bold text-slate-100 block">{{ team.name }}</span>
                      <span class="text-[10px] text-slate-500">{{ team.homeField || 'Main Complex' }}</span>
                    </div>
                  </div>
                </td>

                <!-- League Name -->
                <td class="py-3.5 px-4 text-slate-300 font-medium">
                  {{ team.leagueName || 'Premier Showcase Circuit' }}
                </td>

                <!-- Division -->
                <td class="py-3.5 px-4">
                  <AppBadge variant="primary">{{ team.division }}</AppBadge>
                </td>

                <!-- Coach -->
                <td class="py-3.5 px-4 text-slate-300">
                  {{ team.headCoach }}
                </td>

                <!-- Own Team vs Opponent Badge & Toggle -->
                <td class="py-3.5 px-4 text-center">
                  <button
                    type="button"
                    @click="toggleOwnTeam(team)"
                    :class="[
                      'px-3 py-1 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 mx-auto shadow-sm',
                      team.isOwnTeam
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30'
                        : 'bg-slate-950 text-slate-400 border border-slate-800 hover:text-slate-200'
                    ]"
                  >
                    <component :is="team.isOwnTeam ? Trophy : Swords" class="w-3.5 h-3.5" />
                    <span>{{ team.isOwnTeam ? t('master.ownClub') : t('master.opponent') }}</span>
                  </button>
                </td>

                <!-- Actions -->
                <td class="py-3.5 px-4 text-right">
                  <router-link
                    v-if="team.isOwnTeam"
                    :to="`/dashboard/teams/${team.id}/roster`"
                    class="text-xs text-sky-400 hover:text-sky-300 font-semibold mr-2"
                  >
                    {{ t('master.manageRoster') }}
                  </router-link>
                  <span v-else class="text-[11px] text-slate-500">{{ t('master.scouted') }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- TAB 2: PLAYER MASTER & USER LINKAGE -->
    <!-- ========================================================================= -->
    <div v-if="activeTab === 'PLAYERS'" class="space-y-4">
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div class="relative max-w-sm flex-1">
          <NInput
            v-model:value="playerSearchQuery"
            :placeholder="t('master.searchPlayersPlaceholder')"
            clearable
            size="medium"
          >
            <template #prefix><Search class="w-4 h-4 text-slate-400" /></template>
          </NInput>
        </div>

        <div class="w-64">
          <NSelect
            v-model:value="teamFilter"
            :options="teamFilterOptions"
            size="medium"
          />
        </div>
      </div>

      <!-- Player Master Table with User Linkage Actions -->
      <div class="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="border-b border-slate-800/80 bg-slate-950/40 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                <th class="py-3.5 px-4 text-center w-14">{{ t('master.colJersey') }}</th>
                <th class="py-3.5 px-4">{{ t('master.colPlayerName') }}</th>
                <th class="py-3.5 px-4">{{ t('master.colPrimaryTeam') }}</th>
                <th class="py-3.5 px-4">{{ t('master.colPosition') }}</th>
                <th class="py-3.5 px-4">{{ t('master.colLinkedUser') }}</th>
                <th class="py-3.5 px-4 text-center">{{ t('master.colAccountStatus') }}</th>
                <th class="py-3.5 px-4 text-right">{{ t('master.colUserLinkage') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-800/60 font-sans">
              <tr
                v-for="player in filteredPlayers"
                :key="player.id"
                class="hover:bg-slate-800/40 transition"
              >
                <!-- Jersey -->
                <td class="py-3.5 px-4 text-center font-mono font-extrabold text-sm text-sky-400">
                  #{{ player.jerseyNumber }}
                </td>

                <!-- Athlete Name -->
                <td class="py-3.5 px-4">
                  <span class="font-bold text-slate-100 block">{{ player.firstName }} {{ player.lastName }}</span>
                  <span class="text-[10px] text-slate-500 font-mono">B/T: {{ player.bats }}/{{ player.throws }} &bull; Class {{ player.graduationYear || 2026 }}</span>
                </td>

                <!-- Team -->
                <td class="py-3.5 px-4 text-slate-300">
                  {{ tenantTeams.find(t => t.id === player.teamId)?.name || 'Diamond 18U Premier' }}
                </td>

                <!-- Position -->
                <td class="py-3.5 px-4">
                  <AppBadge variant="position">{{ player.position }}</AppBadge>
                </td>

                <!-- Linked Tenant User Account -->
                <td class="py-3.5 px-4">
                  <div v-if="player.isLinkedToUser" class="flex items-center gap-2">
                    <div class="w-6 h-6 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 font-bold text-[10px]">
                      {{ player.firstName.charAt(0) }}
                    </div>
                    <div>
                      <span class="text-xs font-bold text-emerald-300 block">{{ player.linkedUserFullName || player.firstName }}</span>
                      <span class="text-[10px] text-slate-400 font-mono">{{ player.linkedUserEmail }}</span>
                    </div>
                  </div>
                  <div v-else-if="player.linkStatus === 'INVITED'" class="text-xs text-amber-300 font-mono flex items-center gap-1">
                    <Send class="w-3.5 h-3.5" />
                    <span>{{ t('master.inviteSentText', { email: player.linkedUserEmail || player.email }) }}</span>
                  </div>
                  <div v-else class="text-xs text-slate-500 italic">
                    {{ t('master.notLinked') }}
                  </div>
                </td>

                <!-- Status Badge -->
                <td class="py-3.5 px-4 text-center">
                  <span
                    :class="[
                      'px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1',
                      player.isLinkedToUser
                        ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                        : player.linkStatus === 'INVITED'
                          ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                          : 'bg-slate-950 text-slate-500 border border-slate-800'
                    ]"
                  >
                    <CheckCircle2 v-if="player.isLinkedToUser" class="w-3 h-3" />
                    {{ player.isLinkedToUser ? t('master.linked') : (player.linkStatus === 'INVITED' ? t('master.invited') : t('master.unlinked')) }}
                  </span>
                </td>

                <!-- Linkage Actions -->
                <td class="py-3.5 px-4 text-right">
                  <div class="flex items-center justify-end gap-1.5">
                    <button
                      v-if="!player.isLinkedToUser"
                      type="button"
                      @click="openLinkModal(player)"
                      class="px-2.5 py-1 rounded-xl bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/30 text-sky-300 font-bold text-xs transition flex items-center gap-1"
                    >
                      <Link class="w-3.5 h-3.5" />
                      <span>{{ t('master.linkUserBtn') }}</span>
                    </button>

                    <button
                      v-if="!player.isLinkedToUser && player.linkStatus !== 'INVITED'"
                      type="button"
                      @click="sendInviteLink(player)"
                      class="p-1 rounded-xl text-slate-400 hover:text-amber-400 hover:bg-slate-800 transition"
                      :title="t('master.sendInviteTitle')"
                    >
                      <Send class="w-3.5 h-3.5" />
                    </button>

                    <button
                      v-if="player.isLinkedToUser"
                      type="button"
                      @click="handleUnlinkUser(player)"
                      class="px-2 py-1 rounded-xl bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-300 font-bold text-xs transition flex items-center gap-1"
                      :title="t('master.unlinkTitle')"
                    >
                      <Unlink class="w-3.5 h-3.5" />
                      <span>{{ t('master.unlinkBtn') }}</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- MODAL: LINK PLAYER TO TENANT USER -->
    <!-- ========================================================================= -->
    <AppModal
      v-model="isLinkModalOpen"
      :title="t('master.linkModalTitle')"
      :subtitle="selectedPlayerForLink ? `#${selectedPlayerForLink.jerseyNumber} ${selectedPlayerForLink.firstName} ${selectedPlayerForLink.lastName}` : ''"
      max-width="md"
    >
      <form @submit.prevent="handleConfirmUserLink" class="space-y-4">
        <div class="p-3.5 bg-slate-950 border border-slate-800 rounded-2xl flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-sky-500/20 text-sky-400 font-extrabold text-sm flex items-center justify-center">
            #{{ selectedPlayerForLink?.jerseyNumber }}
          </div>
          <div>
            <p class="text-xs font-bold text-slate-100">{{ selectedPlayerForLink?.firstName }} {{ selectedPlayerForLink?.lastName }}</p>
            <p class="text-[11px] text-slate-400">Position: {{ selectedPlayerForLink?.position }} &bull; Class: {{ selectedPlayerForLink?.graduationYear || 2026 }}</p>
          </div>
        </div>

        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('master.linkModalEmailLabel') }}</label>
          <NInput
            v-model:value="linkUserEmail"
            placeholder="athlete.login@example.com"
            required
          />
          <p class="text-[11px] text-slate-500 mt-1">
            {{ t('master.linkModalEmailDesc') }}
          </p>
        </div>

        <div class="pt-3 flex justify-end gap-2 border-t border-slate-800">
          <NButton quaternary @click="isLinkModalOpen = false">{{ t('common.cancel') }}</NButton>
          <NButton type="primary" attr-type="submit" class="font-bold text-xs shadow-md">
            <template #icon><Link class="w-3.5 h-3.5" /></template>
            {{ t('master.linkModalSubmit') }}
          </NButton>
        </div>
      </form>
    </AppModal>

    <!-- ========================================================================= -->
    <!-- MODAL: ADD MASTER TEAM -->
    <!-- ========================================================================= -->
    <AppModal
      v-model="isAddTeamModalOpen"
      :title="t('master.addTeamModalTitle')"
      :subtitle="activeTenant.name"
      max-width="md"
    >
      <form @submit.prevent="handleSaveTeamMaster" class="space-y-4">
        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('master.addTeamModalNameLabel') }}</label>
          <NInput
            v-model:value="newTeamForm.name"
            :placeholder="t('master.addTeamModalNamePlaceholder')"
            required
          />
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('master.addTeamModalLeagueLabel') }}</label>
            <NInput
              v-model:value="newTeamForm.leagueName"
              :placeholder="t('master.addTeamModalLeaguePlaceholder')"
              required
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('master.addTeamModalDivisionLabel') }}</label>
            <NInput
              v-model:value="newTeamForm.division"
              :placeholder="t('master.addTeamModalDivisionPlaceholder')"
              required
            />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('master.addTeamModalCoachLabel') }}</label>
            <NInput
              v-model:value="newTeamForm.headCoach"
              :placeholder="t('master.addTeamModalCoachPlaceholder')"
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('master.addTeamModalDesignationLabel') }}</label>
            <select
              v-model="newTeamForm.isOwnTeam"
              class="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-100"
            >
              <option :value="true">{{ t('master.addTeamModalOwnOption') }}</option>
              <option :value="false">{{ t('master.addTeamModalOpponentOption') }}</option>
            </select>
          </div>
        </div>

        <div class="pt-3 flex justify-end gap-2 border-t border-slate-800">
          <NButton quaternary @click="isAddTeamModalOpen = false">{{ t('common.cancel') }}</NButton>
          <NButton type="primary" attr-type="submit" class="font-bold text-xs">
            {{ t('master.addTeamModalSubmit') }}
          </NButton>
        </div>
      </form>
    </AppModal>
  </div>
</template>